import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { GroupElement } from '@bynder-studio/render-web';
import { type VisualElement } from '@bynder-studio/render-core';
import { Divider, Dropdown, Tooltip } from '@bynder/design-system';
import { IconCopy, IconCreateGroup, IconDelete, IconMore, IconStarredFolder, IconUngroup } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import useElementGeneralActions from 'packages/pages/editor/useElementGeneralActions';
import useGeneralShortcuts from 'packages/hooks/shortcuts/useGeneralShortcuts';
import usePresetShortcuts from 'packages/hooks/shortcuts/usePresetShortcut';
import { parseShortcutForTooltip, WithShortcutLabel } from 'packages/common/WithShortcutLabel';
import { useComponentsContext } from 'packages/common/Components/ComponentsContext';
import {
    collectDesParentsForGrouping,
    getDesUpdateForUngroup,
} from '~/common/editor/timeline/timeline-actions/components/desUtils';
import useEditor from '~/hooks/useEditor';
import AddLayerButton from '~/common/editor/layers/addLayerButton/AddLayerButton';
import { collectTopLevelElements } from '~/common/editor/timeline/timeline-actions/components/utils';
import useForceUpdate from '~/hooks/useForceUpdate';
import { AddElementWrapper, LayerButton } from '../addLayerButton/AddLayerButton.styled';

const LayerHeader = ({ selectedGroups, setSelectedGroups }) => {
    const { openComponentModal } = useComponentsContext();
    const { creativeModel, manipulationRenderer } = useEditor();
    const forceUpdate = useForceUpdate();
    const { translate } = useTranslate();

    // TODO: Fix later
    const selectedElements = manipulationRenderer?.getSelectedElements() || [];

    const isGroup = useMemo(() => {
        const els = selectedElements.filter((el) => !el.parent || !selectedElements.some((e) => e.id === el.parent.id));

        return els.length === 1 && els.every((el) => el instanceof GroupElement);
    }, [selectedElements]);

    const { handleSelection, handleDelete, handleDuplicate, handleLock } = useElementGeneralActions(
        setSelectedGroups,
        selectedGroups,
    );

    const handleAddOrRemoveGroup = useCallback(() => {
        if (isGroup) {
            const elementToSelect = selectedGroups[selectedGroups.length - 1].children.reduce(
                (elA, elB) => ((elA?.renderOrder || 0) > (elB?.renderOrder || 0) ? elA : elB),
                null,
            );

            // Collect DES parent groups whose activeDropdownElementId points to a group being unwrapped
            const singleSelectGroupUpdates = selectedGroups
                .map((group) => getDesUpdateForUngroup(group))
                .filter(Boolean);

            const editorElements = creativeModel.getAllElementsRecursively<VisualElement>('visual');
            const maskedElements = editorElements.filter((el) => el.mask && el.mask.elementId);

            selectedGroups.forEach((group) => {
                creativeModel.unwrapGroupElement(group.id);

                if (maskedElements) {
                    maskedElements.forEach((element) => {
                        if (group.id === element.mask.elementId) {
                            creativeModel.updateElement(element.id, {
                                mask: null,
                            });
                        }
                    });
                }

                if (group.mask && group.mask.elementId) {
                    const sameElementMaskedElements = maskedElements
                        .filter((el) => !el.id === group.id)
                        .filter((el) => el.mask.elementId === group.mask.elementId);

                    if (!sameElementMaskedElements.length) {
                        creativeModel.updateElement(group.mask.elementId, {
                            mask: null,
                        });
                    }
                }
            });

            // Update DES parent groups to point to the new active child after ungroup
            singleSelectGroupUpdates.forEach(({ desGroupId, newActiveChildId }) => {
                creativeModel.updateElement(desGroupId, { activeDropdownElementId: newActiveChildId });
            });

            if (elementToSelect) {
                manipulationRenderer.selectElement(elementToSelect.id);
            } else {
                setSelectedGroups([]);
            }
        } else if (selectedElements.length) {
            // Collect DES parent groups whose activeDropdownElementId is among the elements being grouped
            const desParentGroups = collectDesParentsForGrouping(selectedElements);

            const groupElement = creativeModel.wrapElementsIntoGroup(selectedElements.map((el) => el.id));

            // Update DES parent groups to point to the newly created group
            desParentGroups.forEach((desGroup) => {
                creativeModel.updateElement(desGroup.id, { activeDropdownElementId: groupElement.id });
            });

            manipulationRenderer.selectElement(groupElement.id);
        }
    }, [isGroup, selectedElements, creativeModel, manipulationRenderer, selectedGroups, setSelectedGroups]);

    const handleCreatePreset = useCallback(() => {
        const copiedElements = collectTopLevelElements(manipulationRenderer.getSelectedElements());

        if (!copiedElements.length) {
            return;
        }

        openComponentModal('create', copiedElements);
    }, [openComponentModal, manipulationRenderer]);

    const handleHide = useCallback(() => {
        collectTopLevelElements(manipulationRenderer?.getSelectedElements()).forEach((el) => {
            creativeModel.updateElement(el.id, { hidden: !el.hidden });
        });
        forceUpdate();
    }, [creativeModel, manipulationRenderer, forceUpdate]);

    useGeneralShortcuts({
        handleDuplicate,
        handleSelection,
        handleGroup: handleAddOrRemoveGroup,
        handleUnGroup: handleAddOrRemoveGroup,
        handleDelete,
        handleLock,
        handleHide,
    });
    usePresetShortcuts({ createPreset: handleCreatePreset });

    const showActionButtons = !!selectedElements.length;

    return (
        <div className="layers--header position-relative">
            <StyledHeader>{translate('editor.layers.sidebar.header.layers')}</StyledHeader>
            <div className="layers--header-action-btns d-flex align-items-center">
                {showActionButtons && (
                    <Tooltip
                        content={
                            !isGroup
                                ? `${translate('editor.layers.sidebar.header.group')} (${parseShortcutForTooltip([
                                      'cmd',
                                      'G',
                                  ])})`
                                : `${translate('editor.layers.sidebar.header.ungroup')} (${parseShortcutForTooltip([
                                      'shift',
                                      'cmd',
                                      'G',
                                  ])})`
                        }
                    >
                        <AddElementWrapper>
                            <LayerButton
                                title=""
                                variant="clean"
                                isDisabled={!selectedElements.length}
                                onClick={handleAddOrRemoveGroup}
                                icon={!isGroup ? <IconCreateGroup /> : <IconUngroup />}
                            />
                        </AddElementWrapper>
                    </Tooltip>
                )}
                {showActionButtons && (
                    <Tooltip
                        content={`${translate('editor.layers.sidebar.header.duplicate')} (${parseShortcutForTooltip([
                            'cmd',
                            'D',
                        ])})`}
                    >
                        <AddElementWrapper>
                            <LayerButton title="" variant="clean" onClick={handleDuplicate} icon={<IconCopy />} />
                        </AddElementWrapper>
                    </Tooltip>
                )}
                {showActionButtons && (
                    <Dropdown
                        position="bottom-right"
                        trigger={({ isOpen, ...triggerProps }) => (
                            <Tooltip content={translate('editor.layers.sidebar.header.moreOptions')}>
                                <AddElementWrapper>
                                    <LayerButton
                                        title=""
                                        icon={<IconMore />}
                                        variant="clean"
                                        isDisabled={false}
                                        {...triggerProps}
                                    />
                                </AddElementWrapper>
                            </Tooltip>
                        )}
                    >
                        <Dropdown.Item icon={<IconStarredFolder />} onClick={handleCreatePreset}>
                            <WithShortcutLabel shortcut={['cmd', 'P']}>
                                {translate('editor.layers.sidebar.header.components.save')}
                            </WithShortcutLabel>
                        </Dropdown.Item>
                        <Divider />
                        <Dropdown.Item icon={<IconDelete />} onClick={handleDelete}>
                            <WithShortcutLabel shortcut={['backspace']}>
                                {translate('editor.layers.sidebar.header.delete')}
                            </WithShortcutLabel>
                        </Dropdown.Item>
                    </Dropdown>
                )}
                <AddLayerButton />
            </div>
        </div>
    );
};

export default LayerHeader;

const StyledHeader = styled.span`
    flex-grow: 1;
`;
